/**
 * Thread unico: risposte pubbliche e note interne nello stesso flusso,
 * ordinate per data (ruling 5; prototipo L227-L252, SPEC §3.10). La fusione e
 * lato client perche il backend continua a servire `publicThread` e
 * `internalNotes` come due liste distinte (`TicketDetail.tsx:652-728`).
 */
import { initialsOf } from '../constants';
import { photoUrl } from '../../../utils/photoUrl';
import type {
  Attachment,
  InternalNote,
  PendingReply,
  PendingReplyStatus,
  ThreadMessage,
} from '../../../services/supportApi';
import { isEmailNote, unwrapEmailText } from './emailText';

/** `draft` (Task 46): risposta in bozza, solo vista operatore. */
export type S3ThreadKind = 'in' | 'out' | 'note' | 'draft';

/** Dati della bozza mostrata nel thread (Task 46). */
export interface S3DraftInfo {
  id: string;
  status: PendingReplyStatus;
  /** L'operatore corrente e l'autore: puo correggerla o ritirarla. */
  isAuthor: boolean;
  /** Stato scelto dall'autore nel popup di invio: si applica all'approvazione. */
  thenStatus?: string;
  updatedAt?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNote?: string;
}

export interface S3ThreadItem {
  key: string;
  kind: S3ThreadKind;
  /**
   * Id del messaggio nel thread pubblico (sottodocumento Mongo): c'e solo sui
   * messaggi pubblici e serve alla modifica del testo. Le note non ce l'hanno.
   */
  id?: string;
  /** Data della correzione del testo, quando c'e stata. */
  editedAt?: string;
  /** Nome di chi ha corretto: solo nella vista operatore. */
  editedByName?: string;
  /** Nota interna ancora da leggere per l'operatore all'apertura (Task 23/B). */
  isNew?: boolean;
  authorName: string;
  initials: string;
  /**
   * URL assoluto della foto dell'autore (associati e rappresentanti), quando il
   * backend la manda: `authorFoto` esiste SOLO nella vista operatore. Gli utenti
   * admin e gli ospiti non hanno foto e restano con le iniziali.
   */
  photo?: string;
  body: string;
  /**
   * Testo da MOSTRARE quando differisce da `body`: le email in testo semplice
   * arrivano con gli a capo del client ogni ~70 caratteri e qui sono riunite
   * (`unwrapEmailText`). La modifica del messaggio lavora sempre su `body`.
   */
  displayBody?: string;
  sentAt: string;
  attachments: Attachment[];
  badge: string | null;
  /** Risposta nata come bozza: chi l'ha approvata (Task 46, solo operatori). */
  approvedByName?: string;
  /** Solo sulle voci `draft` (Task 46). */
  draft?: S3DraftInfo;
  /** Nomi menzionati nella nota (Task 48): i loro `@Nome` si evidenziano. */
  mentionNames?: string[];
  /** L'operatore corrente e fra i menzionati della nota (Task 48). */
  mentionsMe?: boolean;
  /**
   * Note interne (24/10/2026): l'operatore corrente puo correggerla o
   * eliminarla. Li calcola il backend (permesso o autore entro 15 minuti).
   */
  canEditNote?: boolean;
  canDeleteNote?: boolean;
  /** ISO: oltre questa data l'autore senza permesso non puo piu (finestra di 15 minuti). */
  editableUntil?: string;
}

/** Pezzo del testo di una nota: testo semplice o `@Nome` menzionato. */
export interface MentionSegment {
  text: string;
  mention: boolean;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Divide il testo nei `@Nome` menzionati e nel resto (Task 48). Si evidenziano
 * solo i nomi di `mentions[]` della nota, non ogni `@` scritto a mano; a parita
 * di inizio vince il nome piu lungo («@Anna Maria Neri» prima di «@Anna Maria»).
 */
export const splitMentions = (text: string, names: string[] | undefined): MentionSegment[] => {
  const list = Array.from(new Set((names || []).filter((n) => n && n.trim()))).sort((a, b) => b.length - a.length);
  if (!list.length || !text) return [{ text, mention: false }];
  const re = new RegExp(`@(?:${list.map(escapeRe).join('|')})`, 'g');
  const out: MentionSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), mention: false });
    out.push({ text: m[0], mention: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), mention: false });
  return out.length ? out : [{ text, mention: false }];
};

/** Testo da mostrare solo se la riunione delle righe ha cambiato qualcosa. */
const emailDisplay = (body: string, isEmail: boolean): string | undefined => {
  if (!isEmail) return undefined;
  const unwrapped = unwrapEmailText(body);
  return unwrapped === body ? undefined : unwrapped;
};

/** Chi scrive dall'esterno e "in", il canale (utente admin o rappresentante) e "out". */
const kindOf = (authorType: string): S3ThreadKind =>
  authorType === 'associate' || authorType === 'guest' ? 'in' : 'out';

/**
 * A parita di data il messaggio pubblico precede la nota: senza questo rango
 * l'ordine dipenderebbe dalla stabilita di `Array.sort`, e una nota scritta
 * nello stesso minuto della risposta potrebbe scambiarsi di posto fra un
 * render e l'altro. Il secondo criterio e l'indice nella lista d'origine, cosi
 * l'ordine interno a ciascuna lista resta quello del backend.
 */
interface Sortable { item: S3ThreadItem; rank: number; index: number; time: number }

/**
 * Una data illeggibile (campo corrotto, import legacy) darebbe `NaN`, e un
 * `NaN` nel comparatore rende l'ordinamento incoerente: `a-b` e falso in ogni
 * confronto e il risultato dipende dall'algoritmo di sort. Vale 0, cioe
 * "inizio thread", e il rango decide il resto.
 */
const timeOf = (iso: string): number => {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
};

export const mergeThread = (
  publicThread: ThreadMessage[],
  internalNotes: InternalNote[],
  opts: {
    publicOnly?: boolean;
    noteAuthorFallback?: string;
    unreadNoteIds?: string[];
    /** Per indice di `publicThread`: messaggio arrivato per email (`emailMessageFlags`). */
    emailFlags?: boolean[];
    /**
     * Bozze di risposta (Task 46): le `pending` compaiono a tutti gli operatori
     * nel punto cronologico in cui sono state scritte, le `rejected` solo al
     * loro autore, le `approved` mai (c'e gia il messaggio pubblico).
     * Restano anche con `publicOnly`: sono cose da fare, non note.
     */
    pendingReplies?: PendingReply[];
    /** Id del rappresentante corrente (autore delle bozze); assente per gli admin. */
    selfRepresentativeId?: string;
    /**
     * Operatore corrente (Task 48): le note che lo menzionano portano «Ti ha
     * menzionato». `actorType` 'user' per gli admin, 'representative' altrove.
     */
    selfActor?: { actorType: string; actorId: string };
  } = {}
): S3ThreadItem[] => {
  const newNotes = new Set(opts.unreadNoteIds || []);
  const me = opts.selfActor && opts.selfActor.actorId ? opts.selfActor : null;
  const messages: Sortable[] = (publicThread || []).map((m, i) => {
    const kind = kindOf(m.authorType);
    return {
      rank: 0,
      index: i,
      time: timeOf(m.sentAt),
      item: {
        key: `m-${i}-${m.sentAt}`,
        kind,
        id: m._id,
        editedAt: m.editedAt,
        editedByName: m.editedBy?.nome,
        authorName: m.authorName || (kind === 'in' ? 'Richiedente' : 'Canale'),
        initials: initialsOf(m.authorName),
        photo: photoUrl(m.authorFoto),
        body: m.body,
        displayBody: emailDisplay(m.body, Boolean(opts.emailFlags && opts.emailFlags[i])),
        sentAt: m.sentAt,
        attachments: m.attachments || [],
        badge: kind === 'out' ? 'RISPOSTA' : null,
        ...(m.approvedBy ? { approvedByName: m.approvedBy.nome || 'responsabile del canale' } : {}),
      },
    };
  });

  // Note eliminate (27/10/2026): spariscono dal thread. Il backend non le
  // manda piu; se ne arrivasse una (`deletedAt`), qui si scarta. L'indice resta
  // quello della lista d'origine (chiave e ordinamento stabili).
  const notes: Sortable[] = opts.publicOnly
    ? []
    : (internalNotes || [])
        .map((n, i) => ({ n, i }))
        .filter(({ n }) => n && !n.deletedAt)
        .map(({ n, i }) => ({
          rank: 1,
          index: i,
          time: timeOf(n.sentAt),
          item: {
            key: `n-${i}-${n.sentAt}`,
            kind: 'note' as const,
            id: n._id,
            isNew: Boolean(n._id && newNotes.has(n._id)),
            // Nell'area rappresentante le note le scrivono i rappresentanti: il
            // nome generico giusto lo conosce solo il chiamante.
            authorName: n.authorName || opts.noteAuthorFallback || 'Operatore',
            // Senza foto l'iniziale resta `N` (SPEC §3.10, L638): l'avatar ambra
            // dice "nota", non "chi l'ha scritta". Con la foto dell'autore il
            // riconoscimento e immediato e la natura interna resta detta dal
            // badge "NOTA INTERNA" e dalla bolla ambra.
            initials: 'N',
            photo: photoUrl(n.authorFoto),
            body: n.body,
            // Nota scritta da un operatore rispondendo per email (poller della posta).
            displayBody: emailDisplay(n.body, isEmailNote(n.authorName)),
            sentAt: n.sentAt,
            attachments: n.attachments || [],
            badge: 'NOTA INTERNA',
            // Modifica (24/10/2026): campi solo se presenti.
            ...(n.editedAt ? { editedAt: n.editedAt, editedByName: n.editedBy?.nome } : {}),
            ...(n.canEdit ? { canEditNote: true } : {}),
            ...(n.canDelete ? { canDeleteNote: true } : {}),
            ...(n.editableUntil ? { editableUntil: n.editableUntil } : {}),
            // Task 48: senza menzioni la voce resta identica a prima.
            ...(n.mentions && n.mentions.length
              ? {
                  mentionNames: n.mentions.map((x) => x.nome || '').filter(Boolean),
                  mentionsMe: Boolean(
                    me &&
                      n.mentions.some((x) => x.actorType === me.actorType && String(x.actorId) === String(me.actorId))
                  ),
                }
              : {}),
          },
        }));

  const self = opts.selfRepresentativeId ? String(opts.selfRepresentativeId) : '';
  const drafts: Sortable[] = (opts.pendingReplies || [])
    .map((d, i) => ({ d, i }))
    .filter(({ d }) => {
      if (d.status === 'pending') return true;
      if (d.status !== 'rejected') return false;
      return Boolean(self) && d.author?.actorType === 'representative' && String(d.author.actorId) === self;
    })
    .map(({ d, i }) => {
      const isAuthor = Boolean(self) && d.author?.actorType === 'representative' && String(d.author.actorId) === self;
      const authorName = d.author?.nome || 'Operatore';
      return {
        rank: 2,
        index: i,
        time: timeOf(d.createdAt),
        item: {
          key: `d-${d._id}`,
          kind: 'draft' as const,
          id: d._id,
          authorName,
          initials: initialsOf(authorName),
          body: d.body,
          sentAt: d.createdAt,
          attachments: d.attachments || [],
          badge: d.status === 'rejected' ? 'BOZZA RIFIUTATA' : 'BOZZA',
          draft: {
            id: d._id,
            status: d.status,
            isAuthor,
            thenStatus: d.thenStatus || undefined,
            updatedAt: d.updatedAt,
            reviewedByName: d.reviewedBy?.nome,
            reviewedAt: d.reviewedAt,
            reviewNote: d.reviewNote,
          },
        },
      };
    });

  return [...messages, ...notes, ...drafts]
    .sort((a, b) => a.time - b.time || a.rank - b.rank || a.index - b.index)
    .map((s) => s.item);
};

export const threadItemVisual = (kind: S3ThreadKind) => ({
  bubbleClass: `s3-bubble s3-bubble--${kind}`,
  avatarTone: (kind === 'out' ? 'green' : kind === 'note' ? 'amber' : 'chip') as 'green' | 'chip' | 'amber',
  rowDirection: (kind === 'out' ? 'row-reverse' : 'row') as 'row' | 'row-reverse',
});
